import React from 'react';

interface Exam {
  id: string;
  title: string;
  icon: string;
  description: string | React.ReactNode;
  duration: string;
  gradient: string;
}

export function ExamsSection() {
  const exams: Exam[] = [
    {
      id: 'ressonancia',
      title: 'Ressonância Magnética',
      icon: 'ri-brain-line',
      description: 'Exame de alta precisão que utiliza campos magnéticos para gerar imagens detalhadas de órgãos e tecidos internos, sem uso de radiação. Essencial para diagnóstico neurológico, ortopédico e de tecidos moles.',
      duration: '30 a 60 minutos',
      gradient: 'from-blue-500 to-teal-500'
    },
    {
      id: 'tomografia',
      title: 'Tomografia Computadorizada',
      icon: 'ri-scan-line',
      description: 'Exame de imagem que utiliza raios X e tecnologia avançada para produzir cortes detalhados do corpo, permitindo a visualização precisa de órgãos, ossos e tecidos. Rápido e essencial para diagnóstico e acompanhamento de diversas condições.',
      duration: '15 a 30 minutos',
      gradient: 'from-teal-500 to-blue-500'
    },
    {
      id: 'raio-x',
      title: 'Radiografia (Raio X)',
      icon: 'ri-body-scan-line',
      description: 'Exame simples e rápido que utiliza baixas doses de radiação para avaliar estruturas do corpo, como ossos, pulmões e articulações. Fundamental no diagnóstico de diversas condições.',
      duration: '5 a 10 minutos',
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      id: 'ultrassom',
      title: 'Ultrassonografia',
      icon: 'ri-pulse-line',
      description: (
        <span>
          Exame que utiliza ondas sonoras e tecnologia avançada para visualizar órgãos, tecidos e estruturas internas em tempo real. Seguro, indolor e sem radiação.
          <br /><br />
          <strong>Ultrassonografia 3D:</strong> imagens tridimensionais com maior detalhamento anatômico de diversos órgãos, estruturas e do feto.
          <br /><br />
          <strong>Doppler:</strong> avaliação do fluxo sanguíneo nos vasos, importante para diagnóstico circulatório e no acompanhamento gestacional.
        </span>
      ),
      duration: '20 a 40 minutos',
      gradient: 'from-indigo-500 to-cyan-500'
    },
    {
      id: 'mamografia',
      title: 'Mamografia Digital',
      icon: 'ri-heart-pulse-line',
      description: 'Exame de imagem indicado para diagnóstico precoce do câncer de mama. Permite identificar alterações e nódulos ainda em estágio inicial.',
      duration: '15 a 20 minutos',
      gradient: 'from-pink-500 to-rose-500'
    },
    {
      id: 'densitometria',
      title: 'Densitometria Óssea',
      icon: 'ri-bone-line',
      description: 'Exame rápido e indolor que mede a densidade óssea, auxiliando na detecção precoce da osteoporose e na avaliação do risco de fraturas. Utiliza baixa dose de radiação e é essencial para acompanhamento da saúde óssea.',
      duration: '10 a 15 minutos',
      gradient: 'from-amber-500 to-orange-500'
    }
  ];

  const scrollToAgendamento = () => {
    const element = document.getElementById('agendamento');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const imageMap: Record<string, string> = {
    ressonancia: 'https://static.readdy.ai/image/060e1c25caffef29cb7917b5a22faf7c/6e7881505ac9bea2c016ae2b231262e0.png',
    'raio-x': 'https://static.readdy.ai/image/060e1c25caffef29cb7917b5a22faf7c/bd96193e61e08530a0c173d4550f2ac6.png',
    ultrassom: 'https://static.readdy.ai/image/060e1c25caffef29cb7917b5a22faf7c/8853069f9a0ce1a98f76e7a83e383fcd.png',
    mamografia: 'https://static.readdy.ai/image/060e1c25caffef29cb7917b5a22faf7c/6acb49d80c212640d395763909f56c78.png',
    densitometria: 'https://static.readdy.ai/image/060e1c25caffef29cb7917b5a22faf7c/abfde84e214e3632f867fdac8d5a8bda.png',
    tomografia: 'https://static.readdy.ai/image/060e1c25caffef29cb7917b5a22faf7c/829938c3e0b9e47c9d8ec050bead4e43.png',
  };

  return (
    <section id="exames" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-stethoscope-line"></i>
            <span>Nossos Exames</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Exames Disponíveis
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Oferecemos uma ampla gama de exames de diagnóstico por imagem com equipamentos modernos e equipe especializada.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {exams.map((exam) => (
            <div
              key={exam.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group"
            >
              <div className={`h-2 bg-gradient-to-r ${exam.gradient}`}></div>
              <div className="p-8">
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-28 h-28 flex items-center justify-center flex-shrink-0">
                    <img
                      src={imageMap[exam.id]}
                      alt={exam.title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{exam.title}</h3>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">{exam.description}</p>

                <button
                  onClick={scrollToAgendamento}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-all font-semibold shadow-md hover:shadow-lg cursor-pointer text-sm"
                >
                  Agendar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
